/**
 * Weather Service
 * Fetches current weather data using Open-Meteo API (free, no API key required)
 */

import { logger } from '../utils/logger';

export interface WeatherData {
  temperature: number; // in Celsius - actual temperature
  apparentTemperature: number; // in Celsius - "feels like" temperature (wind chill + humidity)
  weatherCode: number; // WMO Weather interpretation code
  windSpeed: number; // in km/h
  precipitation: number; // in mm
  isDay: boolean;
  precipitationProbability?: number; // in % - probability of precipitation
  cloudCover: number; // in % - cloud cover percentage
}

export interface HourlyWeatherData {
  time: string; // ISO timestamp
  temperature: number;
  apparentTemperature: number;
  weatherCode: number;
  windSpeed: number;
  precipitation: number;
  precipitationProbability: number;
  cloudCover: number;
  isDay: boolean; // Whether it's daytime
}

export interface DailyWeatherData {
  date: string; // ISO date string
  temperatureMax: number;
  temperatureMin: number;
  weatherCode: number;
  windSpeedMax: number;
  precipitationSum: number;
  precipitationProbabilityMax: number;
  cloudCover: number;
}

export interface WeatherCondition {
  description: string;
  isRaining: boolean;
  isSnowing: boolean;
}

/**
 * Interprets WMO weather codes into readable conditions
 * https://open-meteo.com/en/docs
 * WMO Weather interpretation codes (WW):
 * 0: Clear sky
 * 1, 2, 3: Mainly clear, partly cloudy, and overcast
 * 45, 48: Fog and depositing rime fog
 * 51, 53, 55: Drizzle: Light, moderate, and dense intensity
 * 56, 57: Freezing Drizzle: Light and dense intensity
 * 61, 63, 65: Rain: Slight, moderate and heavy intensity
 * 66, 67: Freezing Rain: Light and heavy intensity
 * 71, 73, 75: Snow fall: Slight, moderate, and heavy intensity
 * 77: Snow grains
 * 80, 81, 82: Rain showers: Slight, moderate, and violent
 * 85, 86: Snow showers slight and heavy
 * 95: Thunderstorm: Slight or moderate
 * 96, 99: Thunderstorm with slight and heavy hail
 */
export function interpretWeatherCode(code: number): WeatherCondition {
  // Clear sky
  if (code === 0) return { description: 'Clear sky', isRaining: false, isSnowing: false };

  // Mainly clear / Partly cloudy / Overcast
  if (code === 1) return { description: 'Mainly clear', isRaining: false, isSnowing: false };
  if (code === 2) return { description: 'Partly cloudy', isRaining: false, isSnowing: false };
  if (code === 3) return { description: 'Overcast', isRaining: false, isSnowing: false };

  // Fog
  if (code === 45 || code === 48)
    return { description: 'Foggy', isRaining: false, isSnowing: false };

  // Drizzle
  if (code === 51) return { description: 'Light drizzle', isRaining: true, isSnowing: false };
  if (code === 53) return { description: 'Moderate drizzle', isRaining: true, isSnowing: false };
  if (code === 55) return { description: 'Dense drizzle', isRaining: true, isSnowing: false };
  if (code === 56 || code === 57)
    return { description: 'Freezing drizzle', isRaining: true, isSnowing: false };

  // Rain
  if (code === 61) return { description: 'Slight rain', isRaining: true, isSnowing: false };
  if (code === 63) return { description: 'Moderate rain', isRaining: true, isSnowing: false };
  if (code === 65) return { description: 'Heavy rain', isRaining: true, isSnowing: false };
  if (code === 66 || code === 67)
    return { description: 'Freezing rain', isRaining: true, isSnowing: false };

  // Snow
  if (code === 71) return { description: 'Slight snow', isRaining: false, isSnowing: true };
  if (code === 73) return { description: 'Moderate snow', isRaining: false, isSnowing: true };
  if (code === 75) return { description: 'Heavy snow', isRaining: false, isSnowing: true };
  if (code === 77) return { description: 'Snow grains', isRaining: false, isSnowing: true };

  // Rain showers
  if (code === 80) return { description: 'Slight rain showers', isRaining: true, isSnowing: false };
  if (code === 81)
    return { description: 'Moderate rain showers', isRaining: true, isSnowing: false };
  if (code === 82)
    return { description: 'Violent rain showers', isRaining: true, isSnowing: false };

  // Snow showers
  if (code === 85 || code === 86)
    return { description: 'Snow showers', isRaining: false, isSnowing: true };

  // Thunderstorm
  if (code === 95) return { description: 'Thunderstorm', isRaining: true, isSnowing: false };
  if (code === 96 || code === 99)
    return { description: 'Thunderstorm with hail', isRaining: true, isSnowing: false };

  return { description: 'Unknown', isRaining: false, isSnowing: false };
}

/**
 * Gets the appropriate weather icon component name based on weather code and time of day
 * Maps WMO weather codes to Material-UI icons
 */
export function getWeatherIcon(code: number, isDay: boolean): string {
  // Clear sky (0)
  if (code === 0) return isDay ? 'WbSunny' : 'NightsStay';

  // Mainly clear (1)
  if (code === 1) return isDay ? 'WbSunny' : 'NightsStay';

  // Partly cloudy (2)
  if (code === 2) return isDay ? 'WbCloudy' : 'CloudQueue';

  // Overcast (3)
  if (code === 3) return 'Cloud';

  // Fog (45, 48)
  if (code === 45 || code === 48) return 'Foggy';

  // Drizzle (51, 53, 55, 56, 57)
  if (code >= 51 && code <= 57) return 'Grain';

  // Rain (61, 63, 65, 66, 67)
  if (code >= 61 && code <= 67) return 'WaterDrop';

  // Snow (71, 73, 75, 77)
  if (code >= 71 && code <= 77) return 'AcUnit';

  // Rain showers (80, 81, 82)
  if (code >= 80 && code <= 82) return 'Umbrella';

  // Snow showers (85, 86)
  if (code === 85 || code === 86) return 'AcUnit';

  // Thunderstorm (95, 96, 99)
  if (code >= 95 && code <= 99) return 'Thunderstorm';

  // Default fallback
  return 'Cloud';
}

/**
 * Gets user's approximate location using IP geolocation
 * Note: This is only used as fallback. The app should use the LocationContext
 * for centralized location management to avoid multiple location requests.
 */
export async function getUserLocation(): Promise<{ latitude: number; longitude: number } | null> {
  logger.debug('getUserLocation: Starting location fetch');

  try {
    // Try to get precise location from browser
    if (navigator.geolocation) {
      logger.debug('getUserLocation: Geolocation API available, requesting position...');
      try {
        const startTime = Date.now();
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            position => {
              logger.debug('getUserLocation: Got position callback', {
                elapsed: Date.now() - startTime,
                accuracy: position.coords.accuracy,
              });
              resolve(position);
            },
            error => {
              logger.warn('getUserLocation: Position error callback', {
                code: error.code,
                message: error.message,
                elapsed: Date.now() - startTime,
              });
              reject(error);
            },
            {
              timeout: 3000, // 3s timeout - fall back to IP quickly if GPS unavailable
              maximumAge: 300000, // Accept cached position up to 5 min old
              enableHighAccuracy: false, // Faster response, lower battery
            }
          );
        });
        logger.debug('getUserLocation: Successfully got browser position', {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
      } catch (geoError) {
        const errorCode = geoError instanceof GeolocationPositionError ? geoError.code : 'unknown';
        const errorMsg =
          geoError instanceof Error
            ? geoError.message
            : geoError instanceof GeolocationPositionError
              ? geoError.message
              : String(geoError);
        logger.warn('getUserLocation: Geolocation failed, falling back to IP', {
          code: errorCode,
          error: errorMsg,
        });
      }
    } else {
      logger.warn('getUserLocation: Geolocation API not available in this browser');
    }
  } catch (error) {
    logger.warn('getUserLocation: Unexpected error getting precise location', {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  // Fallback to IP-based geolocation - try multiple services
  const ipServices = [
    {
      name: 'ipapi.co',
      url: 'https://ipapi.co/json/',
      getLat: (d: any) => d.latitude,
      getLon: (d: any) => d.longitude,
    },
    {
      name: 'ip-api.com',
      url: 'http://ip-api.com/json/',
      getLat: (d: any) => d.lat,
      getLon: (d: any) => d.lon,
    },
  ];

  for (const service of ipServices) {
    logger.debug(`getUserLocation: Trying IP geolocation via ${service.name}`);
    try {
      const response = await fetch(service.url, { signal: AbortSignal.timeout(5000) });
      const data = await response.json();
      const lat = service.getLat(data);
      const lon = service.getLon(data);
      if (lat && lon) {
        logger.debug(`getUserLocation: Got location from ${service.name}`, {
          lat,
          lon,
          city: data.city,
          country: data.country_name || data.country,
        });
        return { latitude: lat, longitude: lon };
      }
      logger.warn(`getUserLocation: ${service.name} response missing coordinates`, { data });
    } catch (error) {
      logger.warn(`getUserLocation: ${service.name} failed`, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  logger.error('getUserLocation: All location methods failed, returning null');
  return null;
}

/**
 * Fetches current weather data for a specific location using ECMWF model
 * @param latitude - Latitude coordinate
 * @param longitude - Longitude coordinate
 */
export async function getCurrentWeatherForLocation(
  latitude: number,
  longitude: number
): Promise<WeatherData | null> {
  try {
    // Note: ECMWF API doesn't support 'current' parameter, so we get hourly data and use the current hour
    const url = `https://api.open-meteo.com/v1/ecmwf?latitude=${latitude}&longitude=${longitude}&hourly=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,precipitation,precipitation_probability,cloud_cover,is_day&timezone=auto&forecast_days=1`;

    logger.debug('Fetching weather from API', { latitude, longitude });
    const response = await fetch(url);
    logger.debug('Weather API response received', { status: response.status });

    const data = await response.json();

    if (data.hourly && data.hourly.time && data.hourly.time.length > 0) {
      // Find the current hour or the closest future hour
      const now = new Date();
      let index = 0;

      for (let i = 0; i < data.hourly.time.length; i++) {
        const hourTime = new Date(data.hourly.time[i]);
        if (hourTime <= now) {
          index = i;
        } else {
          break;
        }
      }

      logger.debug('Using hourly data', { index, time: data.hourly.time[index] });
      logger.debug('Weather hourly data retrieved', {
        temperature: data.hourly.temperature_2m[index],
        apparent_temperature: data.hourly.apparent_temperature[index],
        weather_code: data.hourly.weather_code[index],
        wind_speed: data.hourly.wind_speed_10m[index],
        precipitation: data.hourly.precipitation[index],
        is_day: data.hourly.is_day[index],
        cloud_cover: data.hourly.cloud_cover[index],
      });

      const weatherCode = data.hourly.weather_code[index];
      const cloudCover = data.hourly.cloud_cover[index] || 0;

      return {
        temperature: Math.round(data.hourly.temperature_2m[index]),
        apparentTemperature: Math.round(data.hourly.apparent_temperature[index]),
        weatherCode,
        windSpeed: Math.round(data.hourly.wind_speed_10m[index]),
        precipitation: data.hourly.precipitation[index],
        isDay: data.hourly.is_day[index] === 1,
        precipitationProbability: data.hourly.precipitation_probability[index],
        cloudCover,
      };
    }

    logger.error('No hourly weather data in API response', undefined, { data });
    return null;
  } catch (error) {
    logger.error('Error fetching weather', error);
    return null;
  }
}

/**
 * Fetches hourly weather forecast for the rest of the day using ECMWF model
 * @param latitude - Latitude coordinate
 * @param longitude - Longitude coordinate
 */
export async function getHourlyWeatherForLocation(
  latitude: number,
  longitude: number
): Promise<HourlyWeatherData[]> {
  try {
    const url = `https://api.open-meteo.com/v1/ecmwf?latitude=${latitude}&longitude=${longitude}&hourly=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,precipitation,precipitation_probability,cloud_cover,is_day&timezone=auto&forecast_days=1`;

    const response = await fetch(url);
    const data = await response.json();

    if (data.hourly) {
      const now = new Date();
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);

      // Filter hourly data for remaining hours of the day
      const hourlyData: HourlyWeatherData[] = [];
      for (let i = 0; i < data.hourly.time.length; i++) {
        const time = new Date(data.hourly.time[i]);
        if (time > now && time <= endOfDay) {
          const weatherCode = data.hourly.weather_code[i];
          const cloudCover = data.hourly.cloud_cover[i] || 0;
          const isDay = data.hourly.is_day[i] === 1;

          hourlyData.push({
            time: data.hourly.time[i],
            temperature: Math.round(data.hourly.temperature_2m[i]),
            apparentTemperature: Math.round(data.hourly.apparent_temperature[i]),
            weatherCode,
            windSpeed: Math.round(data.hourly.wind_speed_10m[i]),
            precipitation: data.hourly.precipitation[i],
            precipitationProbability: data.hourly.precipitation_probability[i],
            cloudCover,
            isDay,
          });
        }
      }

      return hourlyData;
    }

    return [];
  } catch (error) {
    logger.error('Error fetching hourly weather', error);
    return [];
  }
}

/**
 * Fetches daily weather forecast for the rest of the week using ECMWF model
 * @param latitude - Latitude coordinate
 * @param longitude - Longitude coordinate
 */
export async function getDailyWeatherForLocation(
  latitude: number,
  longitude: number
): Promise<DailyWeatherData[]> {
  try {
    const url = `https://api.open-meteo.com/v1/ecmwf?latitude=${latitude}&longitude=${longitude}&daily=temperature_2m_max,temperature_2m_min,weather_code,wind_speed_10m_max,precipitation_sum,precipitation_probability_max,cloud_cover_mean&timezone=auto&forecast_days=7`;

    const response = await fetch(url);
    const data = await response.json();

    if (data.daily) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Filter daily data for remaining days of the week (exclude today)
      const dailyData: DailyWeatherData[] = [];
      for (let i = 0; i < data.daily.time.length; i++) {
        const date = new Date(data.daily.time[i]);
        if (date > today) {
          const weatherCode = data.daily.weather_code[i];
          const cloudCover = data.daily.cloud_cover_mean[i] || 0;

          dailyData.push({
            date: data.daily.time[i],
            temperatureMax: Math.round(data.daily.temperature_2m_max[i]),
            temperatureMin: Math.round(data.daily.temperature_2m_min[i]),
            weatherCode,
            windSpeedMax: Math.round(data.daily.wind_speed_10m_max[i]),
            precipitationSum: data.daily.precipitation_sum[i],
            precipitationProbabilityMax: data.daily.precipitation_probability_max[i],
            cloudCover,
          });
        }
      }

      return dailyData;
    }

    return [];
  } catch (error) {
    logger.error('Error fetching daily weather', error);
    return [];
  }
}

/**
 * Fetches current weather data for the user's location
 * @deprecated Use getCurrentWeatherForLocation with LocationContext instead
 */
export async function getCurrentWeather(): Promise<WeatherData | null> {
  try {
    logger.debug('Getting user location');
    const location = await getUserLocation();
    logger.debug('Location result received', { location });

    if (!location) {
      logger.warn('Could not determine location, using default weather');
      return {
        temperature: 15,
        apparentTemperature: 15,
        weatherCode: 0,
        windSpeed: 10,
        precipitation: 0,
        isDay: true,
        cloudCover: 0,
      };
    }

    logger.debug('Fetching weather for location', { location });
    const weatherData = await getCurrentWeatherForLocation(location.latitude, location.longitude);
    logger.debug('Weather data received', { weatherData });
    return weatherData;
  } catch (error) {
    logger.error('Error fetching weather', error);
    return null;
  }
}

/**
 * Gets a cached weather or fetches new data
 * Weather is cached for 30 minutes to avoid excessive API calls
 */
const CACHE_KEY = 'workout_weather_cache_v2'; // v2: Use raw weather codes from ECMWF API
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export async function getCachedWeather(): Promise<WeatherData | null> {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        logger.cache('hit', 'weather', true, { data });
        return data;
      } else {
        logger.cache('expired', 'weather', false);
      }
    } else {
      logger.cache('miss', 'weather', false);
    }
  } catch (error) {
    logger.warn('Error reading weather cache', { error });
  }

  logger.debug('Calling getCurrentWeather()');
  const weather = await getCurrentWeather();
  logger.debug('getCurrentWeather() returned', { weather });

  if (weather) {
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: weather,
          timestamp: Date.now(),
        })
      );
      logger.cache('write', 'weather', true);
    } catch (error) {
      logger.warn('Error caching weather', { error });
    }
  } else {
    logger.error('getCurrentWeather() returned null');
  }

  return weather;
}
